import { Component } from '@theme/component';
import { DialogHistory } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * Cart drawer — a plain-`<div>` modal overlay, NOT a native `<dialog>`.
 *
 * Razorpay Magic Checkout force-closes every `<dialog>` on the page at checkout
 * (`document.querySelectorAll('dialog').forEach(d => d.close())`), which used to snap this drawer
 * shut mid-handoff. Being a `<div>`, the drawer is invisible to that sweep, so we own its whole
 * lifecycle. In exchange we hand-roll the behaviors a modal `<dialog>` provided for free:
 *
 * - Top-layer stacking + backdrop + slide animation — CSS, keyed off the `open` attribute
 *   (sections/cart-drawer.liquid). No JS drives the animation, so nothing can interrupt it.
 * - Background scroll-lock — CSS `html:has(cart-drawer-component[open])` (assets/base.css).
 * - Focus containment — `inert` on every other top-level element while open. Nothing outside the
 *   drawer stays focusable, so Tab can't escape without a hand-rolled trap; focus moves into the
 *   panel on open and returns to the opener on close.
 * - Escape + outside click — Escape here; the outside/backdrop click closes declaratively via the
 *   backdrop's `on:click="cart-drawer-component/close"`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} panel - The sliding panel (role="dialog").
 * @property {HTMLElement} [backdrop] - The dimmed backdrop.
 * @property {HTMLElement} [liveRegion] - Live region for cart-count announcements.
 *
 * @extends {Component<Refs>}
 */
class CartDrawerComponent extends Component {
  requiredRefs = ['panel'];

  /** Browser Back closes the drawer. Shared, element-agnostic helper; see DialogHistory. */
  #history = new DialogHistory({
    key: 'cartDrawerOpen',
    onBack: () => this.close(),
  });

  /** The element focused before the drawer opened; focus returns here on close. @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** Top-level siblings marked `inert` while open, cleared on close. @type {HTMLElement[]} */
  #inertedElements = [];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    // Clear a stale history entry left by a prior page state (see DialogHistory).
    this.#history.reset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#history.destroy();
    this.#removeInteractionListeners();
    this.#unInertSiblings();
  }

  /**
   * Opens the drawer. Public API — the header trigger (`on:click="#cart-drawer/open"`), the
   * add-to-cart auto-open, and the theme editor all call this.
   */
  open = () => {
    if (this.hasAttribute('open')) return;

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    this.setAttribute('open', '');
    this.#inertSiblings();
    this.#addInteractionListeners();
    this.#history.push();

    // Move focus into the panel once it is visible so keyboard/AT users land inside the drawer.
    requestAnimationFrame(() => this.refs.panel.focus());
  };

  /**
   * Closes the drawer. Public API — the close button and backdrop
   * (`on:click="cart-drawer-component/close"`), Escape, and the mobile Back button all call this.
   */
  close = () => {
    if (!this.hasAttribute('open')) return;

    this.removeAttribute('open');
    this.#unInertSiblings();
    this.#removeInteractionListeners();
    // No-op when Back already consumed the entry (DialogHistory tears down before onBack fires).
    this.#history.pop();

    // Return focus to whatever opened the drawer (usually the header cart icon).
    this.#previouslyFocused?.focus?.();
    this.#previouslyFocused = null;
  };

  /**
   * Opens the drawer on a successful product add and announces the new count when open.
   * @param {CartAddEvent} event
   */
  #handleCartAdd = (event) => {
    const { data } = event.detail;
    const isSuccessfulProductAdd = data?.didError !== true && data?.source === 'product-form-component';

    if (isSuccessfulProductAdd) this.open();

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when the drawer is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = this.refs.liveRegion;
    if (!this.hasAttribute('open') || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  #addInteractionListeners() {
    // Listen on document — focus is inside the panel while the drawer is open.
    document.addEventListener('keydown', this.#handleKeydown);
  }

  #removeInteractionListeners() {
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  };

  /**
   * Marks every top-level element except the drawer's own section `inert`, so keyboard and
   * screen-reader users are contained within the drawer. This replaces a hand-rolled focus trap.
   */
  #inertSiblings() {
    const drawerRoot = this.closest('.shopify-section') ?? this;

    for (const element of Array.from(document.body.children)) {
      if (!(element instanceof HTMLElement)) continue;
      if (element === drawerRoot || element.contains(drawerRoot)) continue;
      if (element.hasAttribute('inert')) continue;

      element.setAttribute('inert', '');
      this.#inertedElements.push(element);
    }
  }

  #unInertSiblings() {
    for (const element of this.#inertedElements) element.removeAttribute('inert');
    this.#inertedElements = [];
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
